import {
  useCallback,
  useMemo,
  type ReactNode,
  type Ref,
  useState,
} from 'react';
import { Flex } from '@chakra-ui/react';
import { EmailComposer } from '@/components/shared/email/EmailComposer';
import {
  toSelectOptions,
  toIdString,
} from '@/components/shared/email/emailValue';
import { useInternalEmailRecipientOptions } from '@/components/shared/email/useInternalEmailRecipientOptions';
import { EMAIL_TOOLBAR_NO_ATTACHMENTS } from '@/components/shared/email/emailToolbar';
import { useRecipientPicker } from '@/components/shared/email/recipient-picker';
import { emailCompositionDefaults } from '@kula-ai/components';
import type {
  EmailApiHandle,
  EmailCompositionValue,
} from '@kula-ai/components';
import { toTemplateOptions } from '@/components/ats/EmailTemplates/templateEmailValue';
import { useInternalEmailTemplates } from './internalEmailTemplates';
import type { EmailUser } from '@/components/shared/email/emailValue';
import type { InternalEmailTemplate } from './internalEmailTemplates';
import type { SendInternalEmailValues } from './sendInternalEmailValue';

interface SendInternalEmailComposerProps {
  formik?: any;
  variables?: Record<string, string[]>;
  /** Scopes the picker's job-role section to this candidate's job. */
  jobId?: string | number;
  /** The attach-files trigger, rendered inside the editor toolbar. */
  toolbarStartSlot?: ReactNode;
  /** kula's handle, so the drawer can scroll its own blocked submit into view. */
  apiRef?: Ref<EmailApiHandle>;
  /** What the send will carry, under the editor. */
  footerAddon?: ReactNode;
  /**
   * The template the sender picked, or null when they cleared it. The composer owns
   * the choice; what a template says to attach — document kinds, and its own files —
   * is applied by the drawer, which owns the attachment selection.
   */
  onTemplateApplied?: (_template: InternalEmailTemplate | null) => void;
}

// Built on the shared email preset so this editor keeps the font-size and
// font-family controls the other email composers have — an internal email ships
// as real HTML, so the typography survives.
//
// The no-attachments variant is the right one here for a reason of our own: the
// toolbar's own attachment control only takes files off this device, whereas
// `toolbarStartSlot` offers the candidate's existing documents as well as an
// upload. Two upload affordances in one toolbar is a coin flip for the user.
const TOOLBAR_FEATURES = EMAIL_TOOLBAR_NO_ATTACHMENTS;

/*
 * kula stacks the card's rows behind one border — first row rounded at the top, the
 * rest squared off and pulled up 1px so the seams read as a single control.
 *
 * The template picker is not part of the message, it is how you start one, so it
 * stands apart: its own rounded box with a small gap under it, leaving From as the
 * head of the stack. The editor drops its top radius for the same reason in reverse —
 * the body is the last row of that stack, not a card of its own.
 *
 * The radius goes on the control itself, reached by position (`wrapper > container >
 * control`) rather than by class: react-select builds those class names with emotion
 * and the readable part is stripped from production builds, so a selector written
 * against one would work here and quietly stop working once deployed.
 *
 * What this deliberately does NOT do is clip the wrapper with `overflow-hidden`. The
 * control is exactly as wide as its wrapper, so the clip lands on its 1px side
 * borders and shaves them — the rows come out looking faded down the edges.
 */
const TEMPLATE_SELECT_PROPS = { className: 'mb-2 [&>div>div]:!rounded-md' };
const FROM_SELECT_PROPS = { className: '[&>div>div]:!rounded-t-md' };
const EDITOR_CLASS = 'rounded-t-none';

/*
 * `height: '100%'` is not a fixed height but the basis the editor shrinks from,
 * so it opens filling whatever the rows above leave it rather than at kula's
 * fixed 320px. `resizable` is kula's drag grip — the same affordance the
 * assessment question and scorecard editors offer — there to take space back,
 * and to give it again up to that same full height. It cannot be pulled TALLER
 * than the drawer: flex caps the frame, and the attachment chips under it stay
 * on its bottom edge wherever the grip puts it.
 */
const EDITOR_PROPS = { height: '100%', resizable: true } as const;

const toValue = (
  values: SendInternalEmailValues | undefined
): EmailCompositionValue => ({
  ...emailCompositionDefaults,
  from: toIdString(values?.fromUserObject?.id ?? values?.from),
  to: (values?.to ?? []).map(toIdString),
  cc: (values?.cc ?? []).map(toIdString),
  bcc: (values?.bcc ?? []).map(toIdString),
  subject: values?.subject ?? '',
  content: values?.body ?? '',
  template: null,
});

const toPatch = (
  value: EmailCompositionValue,
  resolveUser: (_id: string) => EmailUser | undefined
): SendInternalEmailValues => {
  const sender = resolveUser(value.from);
  // A role token resolves to no user, so it passes through untouched.
  const toId = (id: string) => resolveUser(id)?.id ?? id;

  return {
    from: sender?.id ?? value.from,
    ...(sender ? { fromUserObject: sender } : {}),
    to: (value.to ? [value.to].flat() : []).map(toId),
    cc: (value.cc ?? []).map(toId),
    bcc: (value.bcc ?? []).map(toId),
    subject: value.subject,
    body: value.content,
  };
};

/**
 * Composition for sending an internal email from a candidate.
 *
 * Same recipient model as the plan editor — teammates, job roles and Kula roles
 * in every row, through the same sectioned picker and the same chips — seeded
 * from whatever the activity was configured with, and editable before sending.
 */
export const SendInternalEmailComposer = ({
  formik,
  variables,
  jobId,
  toolbarStartSlot,
  apiRef,
  footerAddon,
  onTemplateApplied = () => {},
}: SendInternalEmailComposerProps) => {
  const { recipientUsers } = useInternalEmailRecipientOptions();
  // One picker instance across To/Cc/Bcc: they share the section data and the
  // user query, so typing in one does not re-fetch for the others.
  // The menu portals into this node rather than `document.body`: this composer
  // renders inside a kula Drawer (a Radix Dialog with `modal` on), which puts
  // `pointer-events: none` on everything outside the dialog — a body-portalled
  // menu is unclickable and unscrollable, and sits behind the panel besides.
  const [menuHost, setMenuHost] = useState<HTMLDivElement | null>(null);
  const { selectProps: recipientSelectProps } = useRecipientPicker(
    jobId === undefined || jobId === null ? undefined : String(jobId),
    menuHost,
    // This composer addresses the email now, so the job's roles are listed as
    // the people they resolve to. The interview plan, which configures a send
    // for later, deliberately does not.
    { expandJobRolesToPeople: true }
  );

  const toOptions = useMemo(
    () => toSelectOptions(recipientUsers, 'id'),
    [recipientUsers]
  );

  const { data: templates } = useInternalEmailTemplates();
  const templateOptions = useMemo(
    () => toTemplateOptions(templates ?? []),
    [templates]
  );

  // Picking one replaces the subject and body — `useEmailComposer` applies them when
  // this changes. Held here rather than on the drawer: the picker is part of composing,
  // and nothing outside this card reads the choice. It is not sent either; a template
  // is where the wording came from, and what goes out is whatever ends up in the form.
  const [selectedTemplate, setSelectedTemplate] =
    useState<InternalEmailTemplate | null>(null);

  const handleTemplateChange = useCallback(
    (nextId: string | null) => {
      const picked =
        (templates ?? []).find(
          (template) => String(template.id) === String(nextId)
        ) ?? null;
      setSelectedTemplate(picked);
      // A template is not only its wording: it also says which of the candidate's
      // documents a send may attach, and carries files of its own.
      onTemplateApplied(picked);
      // No extra patch: the subject and body arrive through `selectedTemplate`.
      return undefined;
    },
    [templates, onTemplateApplied]
  );

  return (
    <Flex
      flexDir="column"
      flex={1}
      // The kula Email organism and its internal card/stack divs shrink-wrap, so
      // the editor sits at its fixed 320px default and leaves the bottom of the
      // drawer blank. Stretch each level — none has a stable hook, so they are
      // targeted by depth — and the editor's own `height: '100%'` then fills
      // whatever the drawer has left. Same recipe as the slot-selection step.
      //
      // `flex: 1` alone, deliberately: `min-height: 0` here let the same levels
      // shrink BELOW their content, so a composition taller than the drawer was
      // squeezed until kula's card clipped it and the drawer had nothing to
      // scroll. Left at its `auto` default each level grows past the drawer
      // instead, and the drawer's own scroller takes over.
      sx={{
        '& > div:last-of-type': { flex: 1 },
        '& > div:last-of-type > div:first-of-type': { flex: 1 },
        '& > div:last-of-type > div:first-of-type > div': { flex: 1 },
      }}
    >
      <div ref={setMenuHost} />
      <EmailComposer
        formik={formik}
        toValue={(formValues: any) =>
          toValue(formValues as SendInternalEmailValues)
        }
        toPatch={toPatch}
        valueKey="id"
        toOptions={toOptions}
        ccBccUsers={recipientUsers}
        // Labels the selected sender when they are not in the delegatable list —
        // a fresh send opens on the current user, who may have no mailbox
        // connected yet, and without this the row would show an id or nothing.
        savedUsers={[formik?.values?.fromUserObject].filter(Boolean)}
        variables={variables}
        recipientSelectProps={recipientSelectProps}
        // From needs telling separately: `recipientSelectProps` also carries the
        // recipient menu, so it cannot simply be shared with the sender row.
        menuPortalTarget={menuHost}
        toolbarStartSlot={toolbarStartSlot}
        apiRef={apiRef}
        footerAddon={footerAddon}
        toolbarFeatures={TOOLBAR_FEATURES}
        // Offered on every send — from an activity or ad-hoc, composing or editing a
        // queued email. An activity seeds its own wording, and a template is a way to
        // start from something else instead of typing it out.
        templateOptions={templateOptions}
        templateSelectProps={TEMPLATE_SELECT_PROPS}
        fromSelectProps={FROM_SELECT_PROPS}
        editorClassName={EDITOR_CLASS}
        selectedTemplate={selectedTemplate ?? undefined}
        onTemplateChange={handleTemplateChange}
        editorProps={EDITOR_PROPS}
      />
    </Flex>
  );
};
